/**
 * Webhook Configuration Page
 *
 * Manages webhook endpoints per merchant via the Facade Admin API.
 * Per Design 5.1: Table layout (not cards), cursor pagination, CRUD operations
 * with secret lifecycle management.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardContent, TableSkeleton } from '@/components/ui';
import { CursorPagination } from '@/components/ui/cursor-pagination';
import { ApiErrorDisplay } from '@/components/features/error';
import {
  WebhookConfigTable,
  WebhookConfigDialog,
  SecretRevealDialog,
  TestWebhookResult,
  DeleteConfirmation,
  RotateSecretConfirmation,
} from '@/components/features/webhook-config';
import {
  useWebhookConfigs,
  useWebhookConfig,
  useSetWebhookConfig,
  useDeleteWebhookConfig,
  useRotateWebhookSecret,
  useTestWebhook,
} from '@/hooks/use-webhook-config';
import type { WebhookConfig, WebhookTestResult } from '@/types/webhook-config';

export function WebhookConfigPage() {
  const { t } = useTranslation('webhook-config');

  // Cursor-based pagination state
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const currentCursor = cursorStack.length > 0 ? cursorStack[cursorStack.length - 1] : undefined;

  // Data fetching
  const { data, isLoading, error, refetch } = useWebhookConfigs({ cursor: currentCursor });

  // Mutations
  const setConfigMutation = useSetWebhookConfig();
  const deleteConfigMutation = useDeleteWebhookConfig();
  const rotateSecretMutation = useRotateWebhookSecret();
  const testMutation = useTestWebhook();

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<WebhookConfig | null>(null);
  const [secretToReveal, setSecretToReveal] = useState<string | null>(null);
  const [deletingMerchantId, setDeletingMerchantId] = useState<string | null>(null);
  const [rotatingMerchantId, setRotatingMerchantId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<WebhookTestResult | null>(null);

  // Detail view state
  const [viewingMerchantId, setViewingMerchantId] = useState<string | null>(null);
  const { data: viewingConfig } = useWebhookConfig(viewingMerchantId ?? '');

  // Pagination handlers
  const handleNextPage = useCallback((cursor: string) => {
    setCursorStack((prev) => [...prev, cursor]);
  }, []);

  const handlePreviousPage = useCallback(() => {
    setCursorStack((prev) => prev.slice(0, -1));
  }, []);

  // CRUD handlers
  const handleCreateOrEdit = (merchantId: string, webhookUrl: string) => {
    setConfigMutation.mutate(
      { merchantId, webhookUrl },
      {
        onSuccess: (config) => {
          setCreateDialogOpen(false);
          setEditingConfig(null);
          // If the secret is cleartext (not masked), show the reveal dialog
          if (config.hmacSecret && !config.hmacSecret.includes('****')) {
            setSecretToReveal(config.hmacSecret);
          }
        },
      }
    );
  };

  const handleView = (merchantId: string) => {
    setViewingMerchantId(merchantId);
  };

  const handleEdit = (config: WebhookConfig) => {
    setEditingConfig(config);
  };

  const handleTest = (merchantId: string) => {
    setTestResult(null);
    testMutation.mutate(merchantId, {
      onSuccess: (result) => {
        setTestResult(result);
      },
    });
  };

  const handleRotateSecret = (merchantId: string) => {
    setRotatingMerchantId(merchantId);
  };

  const handleRotateConfirm = () => {
    if (!rotatingMerchantId) return;
    rotateSecretMutation.mutate(rotatingMerchantId, {
      onSuccess: (config) => {
        setRotatingMerchantId(null);
        if (config.hmacSecret && !config.hmacSecret.includes('****')) {
          setSecretToReveal(config.hmacSecret);
        }
      },
    });
  };

  const handleDelete = (merchantId: string) => {
    setDeletingMerchantId(merchantId);
  };

  const handleDeleteConfirm = () => {
    if (!deletingMerchantId) return;
    deleteConfigMutation.mutate(deletingMerchantId, {
      onSuccess: () => {
        setDeletingMerchantId(null);
      },
    });
  };

  return (
    <div>
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('common:button.refresh')}
            </Button>
            <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('actions.create', { defaultValue: 'Add Webhook' })}
            </Button>
          </div>
        }
      />

      {/* Loading state */}
      {isLoading && (
        <Card>
          <CardContent className="py-6">
            <TableSkeleton rows={5} cols={6} />
          </CardContent>
        </Card>
      )}

      {/* Error state */}
      {error && <ApiErrorDisplay error={error} onRetry={() => refetch()} />}

      {/* Test result (inline, dismissible) */}
      {testResult && (
        <div className="mb-4">
          <TestWebhookResult result={testResult} />
        </div>
      )}

      {/* Detail view (inline card when viewing a specific merchant) */}
      {viewingMerchantId && viewingConfig && (
        <Card className="mb-4">
          <CardContent className="py-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">{viewingConfig.merchantId}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewingMerchantId(null)}
              >
                {t('common:button.back', { defaultValue: 'Back' })}
              </Button>
            </div>
            <div className="grid gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">URL: </span>
                <span className="font-mono break-all">{viewingConfig.webhookUrl}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Secret: </span>
                <span className="font-mono">{viewingConfig.hmacSecret}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Status: </span>
                <span>{viewingConfig.enabled ? t('status.active') : t('status.inactive')}</span>
              </div>
              {viewingConfig.createdAt && (
                <div>
                  <span className="text-muted-foreground">Created: </span>
                  <span>{new Date(viewingConfig.createdAt).toLocaleString()}</span>
                </div>
              )}
              {viewingConfig.updatedAt && (
                <div>
                  <span className="text-muted-foreground">Updated: </span>
                  <span>{new Date(viewingConfig.updatedAt).toLocaleString()}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button size="sm" variant="outline" onClick={() => handleTest(viewingConfig.merchantId)}>
                {t('test.button')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleRotateSecret(viewingConfig.merchantId)}>
                {t('actions.rotateSecret', { defaultValue: 'Rotate Secret' })}
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleEdit(viewingConfig)}>
                {t('actions.edit', { defaultValue: 'Edit URL' })}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {!isLoading && !error && data && (
        <Card>
          <WebhookConfigTable
            configs={data.data}
            onView={handleView}
            onEdit={handleEdit}
            onTest={handleTest}
            onRotateSecret={handleRotateSecret}
            onDelete={handleDelete}
          />
          <CursorPagination
            hasMore={data.pagination.hasMore}
            nextCursor={data.pagination.nextCursor}
            onNext={handleNextPage}
            onPrevious={handlePreviousPage}
            hasPrevious={cursorStack.length > 0}
            isLoading={isLoading}
          />
        </Card>
      )}

      {/* Create Dialog */}
      <WebhookConfigDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        mode="create"
        onSubmit={handleCreateOrEdit}
        isLoading={setConfigMutation.isPending}
      />

      {/* Edit Dialog */}
      <WebhookConfigDialog
        open={!!editingConfig}
        onOpenChange={(open) => { if (!open) setEditingConfig(null); }}
        mode="edit"
        merchantId={editingConfig?.merchantId}
        currentUrl={editingConfig?.webhookUrl}
        onSubmit={handleCreateOrEdit}
        isLoading={setConfigMutation.isPending}
      />

      {/* Secret Reveal */}
      {secretToReveal && (
        <SecretRevealDialog
          open={!!secretToReveal}
          onOpenChange={(open) => { if (!open) setSecretToReveal(null); }}
          secret={secretToReveal}
          onConfirm={() => setSecretToReveal(null)}
        />
      )}

      {/* Rotate Secret Confirmation */}
      <RotateSecretConfirmation
        open={!!rotatingMerchantId}
        onOpenChange={(open) => { if (!open) setRotatingMerchantId(null); }}
        merchantId={rotatingMerchantId}
        onConfirm={handleRotateConfirm}
        isRotating={rotateSecretMutation.isPending}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmation
        open={!!deletingMerchantId}
        onOpenChange={(open) => { if (!open) setDeletingMerchantId(null); }}
        merchantId={deletingMerchantId}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteConfigMutation.isPending}
      />
    </div>
  );
}
